<template>
  <div class="neural-wrap" @keyup.esc="onEsc">
    <header class="neural-top">
      <input v-model="q" class="search" placeholder="Filter nodes by label or keyword…" />
      <div class="legend">
        <span class="dot user"></span> User
        <span class="dot topic"></span> Topic
        <span class="dot convo"></span> Conversation
      </div>
    </header>

    <div class="neural-grid">
      <!-- GRAPH CANVAS -->
      <section class="canvas-wrap" @click.self="onCanvasBackgroundClick">
        <svg
          ref="svg"
          :width="w"
          :height="h"
          @mousemove="onMouse"
          @wheel.prevent="onWheel"
          @mousedown="onPanStart"
          @mouseup="onPanEnd"
        >
          <g :transform="`translate(${pan.x},${pan.y}) scale(${zoom})`" class="world">
            <!-- edges -->
            <g class="links">
              <line
                v-for="(e,i) in visEdges"
                :key="'e'+i"
                :x1="pos[e.a]?.x" :y1="pos[e.a]?.y"
                :x2="pos[e.b]?.x" :y2="pos[e.b]?.y"
                :class="['edge', {hl: isLinkedToSelection(e)}]"
              />
            </g>

            <!-- nodes -->
            <g class="nodes">
              <g
                v-for="n in visNodes"
                :key="n.id"
                :transform="nodeTransform(n.id)"
                class="node"
                :class="[n.type, {sel: selectedId===n.id, hov: hoverId===n.id}]"
                @click.stop="onNodeClick(n)"
                @mouseenter="hoverId=n.id"
                @mouseleave="hoverId=null"
              >
                <circle :r="radius(n)" />
                <text dy="4" class="label">{{ n.label }}</text>
              </g>
            </g>
          </g>
        </svg>

        <!-- HUD (zoom + crumbs) -->
        <div class="hud">
          <div class="zoom-controls">
            <button class="btn ghost" @click="zoomOut" title="Zoom out">－</button>
            <span class="zoom-val">{{ (zoom*100)|0 }}%</span>
            <button class="btn ghost" @click="zoomIn" title="Zoom in">＋</button>
            <button class="btn ghost" @click="resetView" title="Reset view">⤾</button>
          </div>

          <div class="focus-controls" v-if="focusId">
            <span class="crumb" @click="clearFocus">🧠 All</span>
            <span class="crumb-sep">›</span>
            <span class="crumb active">{{ focusLabel }}</span>
          </div>
        </div>

        <!-- OVERLAY CARD -->
        <transition name="fade">
          <div v-if="cardOpen" class="overlay" @click.self="closeCard">
            <div class="overlay-panel">
              <header class="overlay-head">
                <div>
                  <h4 v-if="cardMode==='convo'">Conversation: {{ cardEdit.title || archiveItem.title || fallbackTitle(archiveItem) }}</h4>
                  <h4 v-else>Topic: {{ cardEdit.title || (selected && selected.label) }}</h4>
                  <small class="muted">{{ fmt(archiveItem.createdAt || archiveItem.updatedAt) }}</small>
                </div>
                <div class="head-actions">
                  <button class="btn ghost" v-if="cardMode==='convo'" @click="exportOne(archiveItem)">⇩ Export</button>
                  <button class="btn ghost" v-if="cardMode==='convo'" @click="copyId(archiveItem.id)">⧉ Copy ID</button>
                  <label class="toggle">
                    <input type="checkbox" v-model="showRaw" />
                    Raw JSON
                  </label>
                  <button class="icon close" @click="closeCard">×</button>
                </div>
              </header>

              <!-- Metadata -->
              <div class="overlay-meta" v-if="cardMode==='convo'">
                <div><b>ID:</b> <code>{{ archiveItem.id }}</code></div>
                <div><b>Messages:</b> {{ (archiveItem.messages || []).length }}</div>
                <div class="full">
                  <b>Keywords:</b>
                  <span v-for="k in (archiveItem.keywords || [])" :key="k" class="chip">#{{ k }}</span>
                  <span v-if="!(archiveItem.keywords||[]).length" class="muted">none</span>
                </div>
              </div>

              <!-- Question -->
              <div class="overlay-block" v-if="cardMode==='convo'">
                <h5>Question (first user message)</h5>
                <p class="q">{{ archiveQuestion || '—' }}</p>
              </div>

              <!-- Thread -->
              <div class="overlay-block thread" v-if="cardMode==='convo'">
                <h5>Conversation</h5>
                <div v-for="(m,i) in (archiveItem.messages || [])" :key="i" :class="['msg', m.role]">
                  <p>{{ m.content }}</p>
                </div>
              </div>

              <!-- Edit -->
              <div class="overlay-block">
                <h5>{{ cardMode==='convo' ? 'Edit Conversation' : 'Edit Topic' }}</h5>
                <div class="form-grid">
                  <label v-if="cardMode==='convo'">
                    <span>Title</span>
                    <input v-model.trim="cardEdit.title" class="input" placeholder="Title"/>
                  </label>

                  <label v-else>
                    <span>Topic Label</span>
                    <input v-model.trim="cardEdit.title" class="input" placeholder="Topic name"/>
                  </label>

                  <label v-if="cardMode==='convo'">
                    <span>Keywords (comma separated)</span>
                    <input
                      :value="(cardEdit.keywords || []).join(', ')"
                      class="input"
                      placeholder="e.g. touring, venues"
                      @input="cardEdit.keywords = $event.target.value.split(',').map(s=>s.trim()).filter(Boolean)"
                    />
                  </label>
                </div>
                <div class="overlay-actions">
                  <button class="btn ghost" @click="saveCardEdits">✔ Save</button>
                  <button v-if="cardMode==='convo'" class="btn warn" @click="deleteFromCard">🗑 Delete</button>
                </div>
              </div>

              <!-- Merge -->
              <div class="overlay-block">
                <h5>Merge</h5>
                <div class="merge-row">
                  <label class="select">
                    <span>{{ cardMode==='convo' ? 'Add conversation to topic' : 'Merge this topic into' }}</span>
                    <select @change="mergeFromCard($event.target.value)">
                      <option value="">Select…</option>
                      <option v-for="t in mergeTargets" :key="t" :value="t">#{{ t }}</option>
                    </select>
                  </label>
                </div>
                <p class="muted" v-if="!mergeTargets.length">
                  No available {{ cardMode==='convo' ? 'topics' : 'target topics' }}.
                </p>
              </div>

              <!-- Raw JSON (optional) -->
              <div v-if="showRaw" class="overlay-block">
                <h5>Raw JSON</h5>
                <pre class="raw">{{ archiveItem }}</pre>
              </div>
            </div>
          </div>
        </transition>
      </section>

      <!-- SIDE PANEL -->
      <aside class="side">
        <div v-if="selected">
          <h4>{{ selected.label }}</h4>
          <small class="muted">Type: {{ selected.type }}</small>

          <div v-if="selected.type==='topic'" class="block">
            <h5>Conversations with #{{ selected.label }}</h5>
            <ul class="list">
              <li
                v-for="c in convosForTopic(selected.label)"
                :key="c.id"
                @click="openInArchive(c.id)"
              >
                {{ c.title || (c.messages && c.messages[0] && c.messages[0].content) || 'Untitled' }}
                <small class="muted"> · {{ (c.messages||[]).length }} msgs</small>
              </li>
            </ul>
          </div>

          <div v-else-if="selected.type==='convo'" class="block">
            <h5>Conversation</h5>
            <p class="muted">{{ summary(selected.item) }}</p>
            <button class="btn ghost" @click="openInArchive(selected.id)">Open in Archive</button>
          </div>

          <div v-else class="block">
            <h5>Overview</h5>
            <p class="muted">Topics: {{ topics.length }} · Conversations: {{ convs.length }}</p>
          </div>
        </div>
        <div v-else class="placeholder">
          <p class="muted">Click a node to see details.</p>
        </div>
      </aside>
    </div>

    <div v-if="error" class="error">⚠️ {{ error }}</div>
  </div>
</template>

<script>
import { ArchiveAPI as RealArchive } from "../utils/storage";
const ArchiveAPI = RealArchive || {};

export default {
  name: "NeuralView",
  data() {
    return {
      q: "",
      list: [],
      error: "",
      w: 900,
      h: 560,
      center: { x: 450, y: 280 },

      selectedId: null,
      hoverId: null,

      zoom: 1,
      pan: { x: 0, y: 0 },
      isPanning: false,
      panStart: null,

      focusId: null, // parent focus (e.g., "user" or "topic:xyz")

      // overlay state
      cardOpen: false,
      cardMode: "convo",          // 'convo' | 'topic'
      archiveItem: {},            // normalized convo object
      archiveQuestion: "",        // first user message
      cardEdit: { title: "", keywords: [] },
      mergeTargets: [],           // topics to merge into
      showRaw: false,
      prevFocusId: null,

      // graph data
      nodes: [],
      edges: [],
      pos: {},
    };
  },
  computed: {
    topics() {
      const set = new Set();
      this.list.forEach(c => (c.keywords || []).forEach(k => set.add(k)));
      return Array.from(set).sort();
    },
    convs() { return this.list; },
    selected() {
      return this.nodes.find(n => n.id === this.selectedId) || null;
    },
    focusLabel() {
      const n = this.nodes.find(n => n.id === this.focusId);
      return n ? n.label : "";
    },
    graphNodes() {
      if (!this.focusId) return this.nodes;
      const keep = new Set([this.focusId]);
      this.edges.forEach(e => {
        if (e.a === this.focusId) keep.add(e.b);
        if (e.b === this.focusId) keep.add(e.a);
      });
      return this.nodes.filter(n => keep.has(n.id));
    },
    graphEdges() {
      if (!this.focusId) return this.edges;
      const ids = new Set(this.graphNodes.map(n => n.id));
      return this.edges.filter(e => ids.has(e.a) && ids.has(e.b));
    },
    visNodes() {
      const q = this.q.trim().toLowerCase();
      const base = this.graphNodes;
      if (!q) return base;
      return base.filter(n =>
        (n.label || "").toLowerCase().includes(q)
        || (n.type === "convo" && (this.summary(n.item) || "").toLowerCase().includes(q))
      );
    },
    visEdges() {
      const ids = new Set(this.visNodes.map(n => n.id));
      return this.graphEdges.filter(e => ids.has(e.a) && ids.has(e.b));
    },
  },
  mounted() {
    const rect = this.$el.getBoundingClientRect();
    if (rect.width) {
      this.w = Math.min(1200, rect.width - 360);
      this.center = { x: this.w / 2, y: this.h / 2 };
    }
    this.refresh();
    window.addEventListener("resize", this.onResize);
    window.addEventListener("archive:changed", this.refresh);
    window.addEventListener("storage", this.refresh);
    window.addEventListener("keydown", this.onHotkeys);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("archive:changed", this.refresh);
    window.removeEventListener("storage", this.refresh);
    window.removeEventListener("keydown", this.onHotkeys);
  },
  methods: {
    /* ---------- DATA ---------- */
    async refresh() {
      this.error = "";
      try {
        if (ArchiveAPI && typeof ArchiveAPI.list === "function") {
          const arr = await ArchiveAPI.list();
          this.list = Array.isArray(arr) ? arr : [];
        } else {
          this.list = [];
        }
        this.buildGraph();
      } catch (e) {
        this.error = e.message || "Failed to load data.";
      }
    },

    /* ---------- GRAPH ---------- */
    getPos(id) { return (this.pos && this.pos[id]) ? this.pos[id] : this.center; },
    nodeTransform(id) {
      const p = this.getPos(id);
      return `translate(${p.x},${p.y})`;
    },
    onResize() {
      const rect = this.$el.getBoundingClientRect();
      if (rect.width) {
        this.w = Math.min(1200, rect.width - 360);
        this.center = { x: this.w / 2, y: this.h / 2 };
        this.layoutPositions();
      }
    },
    buildGraph() {
      const nodes = [];
      const edges = [];

      nodes.push({ id: "user", type: "user", label: "You" });

      const tnodes = this.topics.map(t => ({ id: "topic:" + t, type: "topic", label: t }));
      nodes.push(...tnodes);
      tnodes.forEach(t => edges.push({ a: "user", b: t.id }));

      this.list.forEach(c => {
        const id = c.id || ("convo:" + Math.random().toString(36).slice(2));
        nodes.push({ id, type: "convo", label: (c.title || "Conversation"), item: c });
        edges.push({ a: "user", b: id });
        (c.keywords || []).forEach(k => {
          const tid = "topic:" + k;
          edges.push({ a: tid, b: id });
        });
      });

      this.nodes = nodes;
      this.edges = edges;
      this.layoutPositions();
    },
    layoutPositions() {
      const R1 = Math.min(this.w, this.h) * 0.28;
      const R2 = Math.min(this.w, this.h) * 0.44;
      const pos = {};
      pos["user"] = { x: this.center.x, y: this.center.y };

      const topics = this.nodes.filter(n => n.type === "topic");
      const convos = this.nodes.filter(n => n.type === "convo");

      placeOnRing(topics, R1, this.center, pos);
      placeOnRing(convos, R2, this.center, pos);

      this.nodes.forEach(n => { if (!pos[n.id]) pos[n.id] = { x: this.center.x, y: this.center.y }; });
      this.pos = pos;
    },

    radius(n) {
      if (n.type === "user") return 20;
      if (n.type === "topic") return 12;
      return 9;
    },

    select(id) { this.selectedId = id; },

    isLinkedToSelection(e) {
      if (!this.selectedId) return false;
      return e.a === this.selectedId || e.b === this.selectedId;
    },

    /* ---------- SUMMARIES / FILTERS ---------- */
    summary(c) {
      const m = (c && c.messages) || [];
      const s = m.map(x => x.role + ": " + (x.content || "")).join(" ");
      return s.length > 160 ? s.slice(0, 159) + "…" : s || "No content";
    },
    convosForTopic(topic) {
      return this.list.filter(c => (c.keywords || []).includes(topic));
    },

    /* ---------- FOCUS & NAV ---------- */
    onNodeClick(n) {
      this.select(n.id);
      if (n.type === "user" || n.type === "topic") {
        this.setFocus(n.id);
        this.bumpZoomTo(1.15);
        this.pulseSelection();
      } else if (n.type === "convo") {
        // click a convo opens card directly
        this.openInArchive(n.id);
      }
    },
    setFocus(id) {
      this.focusId = id;
      const p = this.pos[id];
      if (p) {
        const cx = this.w / 2, cy = this.h / 2;
        this.pan.x = cx - p.x * this.zoom;
        this.pan.y = cy - p.y * this.zoom;
      }
    },
    clearFocus() { this.focusId = null; },
    onCanvasBackgroundClick() {
      this.prevFocusId = this.focusId;
      this.clearFocus();
      this.selectedId = null;
    },

    /* ---------- ZOOM / PAN ---------- */
    bumpZoomTo(minZoom=1.1) { if (this.zoom < minZoom) this.zoom = minZoom; },
    pulseSelection() {
      const el = this.$refs.svg; if (!el) return;
      el.classList.remove("spark"); void el.offsetWidth; el.classList.add("spark");
    },
    onWheel(e) {
      const delta = -Math.sign(e.deltaY) * 0.1;
      const old = this.zoom;
      const next = Math.min(3, Math.max(0.4, old + delta));

      const svg = this.$refs.svg;
      if (!svg) { this.zoom = next; return; }
      const pt = svg.createSVGPoint();
      pt.x = e.clientX; pt.y = e.clientY;
      const c = pt.matrixTransform(svg.getScreenCTM().inverse());

      this.pan.x = c.x - (c.x - this.pan.x) * (next/old);
      this.pan.y = c.y - (c.y - this.pan.y) * (next/old);
      this.zoom = next;
    },
    onPanStart(e) {
      this.isPanning = true;
      this.panStart = { x: e.clientX - this.pan.x, y: e.clientY - this.pan.y };
      window.addEventListener("mousemove", this.onPanMove);
    },
    onPanMove(e) {
      if (!this.isPanning) return;
      this.pan.x = e.clientX - this.panStart.x;
      this.pan.y = e.clientY - this.panStart.y;
    },
    onPanEnd() {
      this.isPanning = false;
      window.removeEventListener("mousemove", this.onPanMove);
    },
    zoomIn() { this.zoom = Math.min(3, +(this.zoom + 0.15).toFixed(2)); },
    zoomOut() { this.zoom = Math.max(0.4, +(this.zoom - 0.15).toFixed(2)); },
    resetView() { this.zoom = 1; this.pan = { x: 0, y: 0 }; },
    onHotkeys(e) {
      if (e.target && /input|textarea|select/i.test(e.target.tagName)) return;
      if (e.key === '+') this.zoomIn();
      if (e.key === '-') this.zoomOut();
      if (e.key === '0') this.resetView();
      if (e.key === 'Escape') this.onEsc();
    },
    onEsc() {
      if (this.cardOpen) return this.closeCard();
      if (this.focusId) return this.clearFocus();
      this.selectedId = null;
    },

    /* ---------- ARCHIVE CARD ---------- */
    async openInArchive(id) {
      // freshest object
      let item = null;
      try {
        if (ArchiveAPI && typeof ArchiveAPI.get === "function") {
          item = await ArchiveAPI.get(id);
        }
      } catch (e) { /* non-fatal */ }
      if (!item) item = this.list.find(c => c.id === id) || {};

      this.archiveItem = item; // already normalized by API
      const firstUser = (item.messages || []).find(m => m.role === "user");
      this.archiveQuestion = firstUser ? firstUser.content : "";

      this.cardEdit = {
        title: item.title || "",
        keywords: (item.keywords || []).slice(),
      };

      const sel = this.nodes.find(n => n.id === this.selectedId);
      this.cardMode = sel && sel.type === "topic" ? "topic" : "convo";

      const allTopics = this.topics;
      if (this.cardMode === "convo") {
        const hay = ((item.title || "") + " " + this.summary(item)).toLowerCase();
        this.mergeTargets = allTopics
          .map(t => ({ t, score: hay.includes(String(t).toLowerCase()) ? 1 : 0 }))
          .sort((a,b) => b.score - a.score)
          .map(x => x.t);
      } else {
        const topicLabel = sel ? sel.label : "";
        this.mergeTargets = allTopics.filter(t => t !== topicLabel);
      }

      this.prevFocusId = this.focusId;
      this.cardOpen = true;
      this.$emit("open-archive", id, this.archiveItem);
    },
    closeCard() {
      this.cardOpen = false;
      if (this.prevFocusId) {
        this.focusId = this.prevFocusId;
        this.prevFocusId = null;
      }
      this.showRaw = false;
    },

    async saveCardEdits() {
      try {
        if (this.cardMode === "convo") {
          const id = this.archiveItem.id;
          const payload = {
            title: (this.cardEdit.title || "").trim(),
            keywords: dedupeStrings(this.cardEdit.keywords || []),
          };
          if (ArchiveAPI && typeof ArchiveAPI.update === "function") {
            await ArchiveAPI.update(id, payload);
          } else {
            const c = this.list.find(x => x.id === id);
            if (c) Object.assign(c, payload);
          }
          await this.refresh();
          await this.openInArchive(id);
        } else if (this.cardMode === "topic") {
          const selectedNode = this.nodes.find(n => n.id === this.selectedId);
          if (!selectedNode) return;
          const oldLabel = selectedNode.label;
          const newLabel = (this.cardEdit.title || "").trim();
          if (!newLabel || newLabel === oldLabel) return;

          for (const c of this.list) {
            const kws = (c.keywords || []).slice();
            const i = kws.indexOf(oldLabel);
            if (i >= 0) {
              kws.splice(i, 1, newLabel);
              await this.updateConversationKeywords(c.id, kws);
            }
          }
          await this.refresh();
          const nid = "topic:" + newLabel;
          this.selectedId = nid;
          this.setFocus(nid);
          this.cardEdit.title = newLabel;
          this.mergeTargets = this.topics.filter(t => t !== newLabel);
        }
      } catch (e) {
        this.error = e && e.message ? e.message : "Failed to save edits.";
      }
    },
    async deleteFromCard() {
      if (!this.archiveItem || !this.archiveItem.id) return;
      const ok = window.confirm("Delete this conversation? This can’t be undone.");
      if (!ok) return;
      await this.deleteConversation(this.archiveItem.id);
      this.closeCard();
      await this.refresh();
    },
    async mergeFromCard(target) {
      if (!target) return;
      if (this.cardMode === "convo") {
        await this.mergeConvoIntoTopic(this.archiveItem.id, target);
        const set = new Set(this.cardEdit.keywords || []); set.add(target);
        this.cardEdit.keywords = Array.from(set);
        await this.openInArchive(this.archiveItem.id);
      } else if (this.cardMode === "topic") {
        await this.mergeTopicInto(target);
        this.cardEdit.title = target;
        this.mergeTargets = this.topics.filter(t => t !== target);
      }
    },

    /* ---------- CRUD helpers used above ---------- */
    async updateConversationKeywords(id, keywords) {
      if (ArchiveAPI && typeof ArchiveAPI.update === "function") {
        await ArchiveAPI.update(id, { keywords: dedupeStrings(keywords) });
      } else {
        const c = this.list.find(x => x.id === id);
        if (c) c.keywords = dedupeStrings(keywords);
      }
    },
    async deleteConversation(id) {
      if (ArchiveAPI && typeof ArchiveAPI.remove === "function") {
        await ArchiveAPI.remove(id);
      } else if (ArchiveAPI && typeof ArchiveAPI.delete === "function") {
        await ArchiveAPI.delete(id);
      } else if (ArchiveAPI && typeof ArchiveAPI.destroy === "function") {
        await ArchiveAPI.destroy(id);
      } else {
        this.list = this.list.filter(c => c.id !== id);
      }
      window.dispatchEvent(new CustomEvent("archive:changed"));
    },
    async mergeConvoIntoTopic(convoId, topicLabel) {
      const c = this.list.find(x => x.id === convoId);
      if (!c) return;
      const kws = dedupeStrings([...(c.keywords || []), topicLabel]);
      await this.updateConversationKeywords(convoId, kws);
      await this.refresh();
      this.selectedId = convoId; // keep UX anchored
    },
    async mergeTopicInto(targetTopic) {
      const sel = this.nodes.find(n => n.id === this.selectedId);
      if (!sel || sel.type !== "topic") return;
      const src = sel.label;
      if (src === targetTopic) return;

      for (const c of this.list) {
        const has = (c.keywords || []).includes(src);
        if (has) {
          const next = dedupeStrings([...(c.keywords || []).filter(k => k !== src), targetTopic]);
          await this.updateConversationKeywords(c.id, next);
        }
      }
      await this.refresh();
      this.selectedId = "topic:" + targetTopic;
      this.setFocus(this.selectedId);
    },

    /* ---------- Misc ---------- */
    exportOne(c) {
      const blob = new Blob([JSON.stringify(c, null, 2)], { type: "application/json;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (c.title || this.fallbackTitle(c)).replace(/[^\w-]+/g, "_").slice(0, 64);
      a.href = url;
      a.download = `${safe || "conversation"}.json`;
      a.click();
      URL.revokeObjectURL(url);
    },
    copyId(id) {
      if (!id) return;
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(String(id)).catch(() => {});
      }
    },
    fmt(ts) {
      try { return new Date(ts || Date.now()).toLocaleString(); }
      catch { return ""; }
    },
    fallbackTitle(c) {
      const m = (c && c.messages) || [];
      return (m[0] && m[0].content) ? (m[0].content.length > 64 ? m[0].content.slice(0,63) + "…" : m[0].content) : "Untitled conversation";
    },
    onMouse(e) {
      const svg = this.$refs.svg;
      if (!svg) return;
      const pt = svg.createSVGPoint();
      pt.x = e.clientX; pt.y = e.clientY;
      const cursorpt = pt.matrixTransform(svg.getScreenCTM().inverse());
      const mx = cursorpt.x, my = cursorpt.y;

      let nearest = null, nnd = 1e9, thresh = 24;
      this.visNodes.forEach(n => {
        const p = this.pos[n.id];
        if (p) {
          const d = Math.hypot(p.x - mx, p.y - my);
          if (d < nnd && d < thresh) { nnd = d; nearest = n.id; }
        }
      });
      this.hoverId = nearest;
    },
  },
};

/* ring layout helper */
function placeOnRing(items, R, center, pos) {
  const N = Math.max(1, items.length);
  items.forEach((n, i) => {
    const a = (i / N) * Math.PI * 2 - Math.PI / 2;
    pos[n.id] = { x: center.x + Math.cos(a) * R, y: center.y + Math.sin(a) * R };
  });
}
/* local tiny helper (kept here for convenience) */
function dedupeStrings(arr) {
  return Array.from(new Set((arr || []).map(x => String(x).trim()).filter(Boolean)));
}
</script>

<style scoped>
.neural-wrap{ position:relative; display:flex; flex-direction:column; height:100%; }
.neural-top{
  display:flex; align-items:center; justify-content:space-between; gap:12px;
  padding:10px 12px; border-bottom:1px solid rgba(255,255,255,.12);
  background: linear-gradient(180deg, rgba(0,224,255,.08), transparent 70%);
}
.search{
  flex:1; padding:10px 12px; border-radius:10px; outline:none;
  background: rgba(255,255,255,.06); color:#fff; border:1px solid rgba(255,255,255,.12);
}
.legend{ display:flex; align-items:center; gap:12px; }
.legend .dot{ width:12px; height:12px; border-radius:999px; display:inline-block; margin-right:6px; }
.legend .dot.user{ background:#00e0ff; box-shadow:0 0 12px #00e0ff88; }
.legend .dot.topic{ background:#7c6cff; box-shadow:0 0 12px #7c6cff88; }
.legend .dot.convo{ background:#9ef8c8; box-shadow:0 0 12px #9ef8c888; }

.neural-grid{
  display:grid; grid-template-columns: 1fr 360px; gap:0; flex:1; min-height:0;
}
.canvas-wrap{ position:relative; display:flex; align-items:center; justify-content:center; overflow:hidden; }
svg{ background: radial-gradient(60% 60% at 50% 50%, rgba(255,255,255,.03), rgba(255,255,255,.01)); border-right:1px solid rgba(255,255,255,.12); }
.world{ transition: transform 120ms ease-out; will-change: transform; }
.edge{ stroke: rgba(255,255,255,.18); stroke-width: 1.2; }
.edge.hl{ stroke: #00e0ffaa; stroke-width: 1.8; stroke-dasharray: 6 6; animation: dash 1.6s linear infinite; }

.node circle{ fill: rgba(255,255,255,.08); stroke: rgba(255,255,255,.35); }
.node.user circle{ fill:#00e0ff44; stroke:#00e0ffaa; }
.node.topic circle{ fill:#7c6cff2d; stroke:#7c6cff99; }
.node.convo circle{ fill:#9ef8c824; stroke:#9ef8c888; }

.node.sel circle{ stroke-width: 2.5; animation: breathe-glow 2.2s ease-in-out infinite; }
.node.hov circle{ filter: drop-shadow(0 0 8px rgba(255,255,255,.35)); }

.label{ fill:#e9eaff; font-size:12px; text-anchor: middle; pointer-events: none; transform: translateY(22px); }

.side{ overflow:auto; padding:12px; }
.placeholder{ display:flex; align-items:center; justify-content:center; height:100%; color:#cfd0ff; }

.block{ margin:12px 0; padding:10px; border-radius:12px; background: rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.12); }
.list{ list-style:none; padding:0; margin:0; }
.list li{ padding:6px 0; border-bottom:1px dashed rgba(255,255,255,.1); cursor:pointer; }
.list li:hover{ color:#9ef8c8; }
.muted{ opacity:.85; }

.btn{ padding:6px 12px; border-radius:10px; background:#2a2a45; color:#fff; border:1px solid transparent; }
.btn.ghost{ border-color: rgba(255,255,255,.2); background:rgba(255,255,255,.06); }
.btn.ghost:hover{ background:rgba(0,224,255,.12); }
.btn.warn{ background: rgba(255, 88, 88, .1); border-color: rgba(255, 88, 88, .4); }
.btn.warn:hover{ background: rgba(255, 88, 88, .18); }

.hud{
  position: absolute; left: 12px; bottom: 12px;
  display: flex; gap: 16px; align-items: center;
  background: rgba(0,0,0,.25); padding: 8px 10px; border-radius: 12px;
  border: 1px solid rgba(255,255,255,.12); backdrop-filter: blur(6px);
}
.zoom-controls { display: flex; align-items: center; gap: 8px; }
.zoom-val { min-width: 48px; text-align: center; color: #e9eaff; opacity: .9; }
.focus-controls { display: flex; align-items: center; gap: 8px; }
.crumb { cursor: pointer; color: #9ef8c8; }
.crumb:hover { text-decoration: underline; }
.crumb-sep { opacity: .6; }
.crumb.active { color: #e9eaff; }

/* Overlay */
.fade-enter-active, .fade-leave-active { transition: opacity .15s ease; }
.fade-enter, .fade-leave-to { opacity: 0; }
.overlay{
  position: fixed; inset: 0; background: rgba(0,0,0,.45);
  display:flex; align-items:center; justify-content:center; z-index: 50;
}
.overlay-panel{
  width: min(980px, 92vw); max-height: 86vh; overflow:auto;
  background: #0b0b19; border:1px solid rgba(255,255,255,.12);
  border-radius: 16px; box-shadow: 0 12px 36px rgba(0,0,0,.45);
  padding: 12px 14px;
}
.overlay-head{ display:flex; align-items:flex-start; justify-content:space-between; gap:8px; border-bottom:1px solid rgba(255,255,255,.08); padding-bottom:8px; }
.head-actions{ display:flex; align-items:center; gap:6px; }
.overlay-meta{ display:grid; grid-template-columns: 1fr 1fr; grid-gap:8px; padding:8px 0; }
.overlay-block{ margin:12px 0; padding:10px; border-radius:12px; background: rgba(255,255,255,.04); border:1px solid rgba(255,255,255,.08); }
.overlay-block.thread .msg{ max-width: 88%; padding:10px 12px; border-radius:12px; background: rgba(255,255,255,.06); border:1px solid rgba(255,255,255,.08); margin:8px 0; }
.overlay-block.thread .msg.user{ align-self:flex-end; background:#4e2df214; border-color:#7c6cff66; }
.overlay-block.thread .msg.assistant{ align-self:flex-start; }
.overlay-actions{ display:flex; gap:8px; justify-content:flex-end; }
.icon.close{ border:1px solid rgba(255,255,255,.25); background:transparent; color:#e9eaff; border-radius:10px; padding:4px 10px; }
.q{ font-style: italic; }
.toggle{ display:flex; align-items:center; gap:6px; font-size:12px; opacity:.9; }
.raw{ white-space: pre-wrap; word-break: break-word; font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace; font-size:12px; color:#cfe3ff; }

.error{ padding:10px; color:#ffd9e3; }

@keyframes spark { 0% { filter: drop-shadow(0 0 0 rgba(0,224,255,0)); }
  50% { filter: drop-shadow(0 0 18px rgba(0,224,255,.75)); }
  100% { filter: drop-shadow(0 0 0 rgba(0,224,255,0)); } }
svg.spark { animation: spark 600ms ease-out; filter: drop-shadow(0 0 14px rgba(0,224,255,.45)); }
.edge.hl{ stroke: #00e0ffaa; stroke-width: 1.8; stroke-dasharray: 6 6; animation: dash 1.6s linear infinite; }
@keyframes dash { to { stroke-dashoffset: -24; } }
.node.sel circle{ stroke-width: 2.5; animation: breathe-glow 2.2s ease-in-out infinite; }
@keyframes breathe-glow { 0%,100% { filter: drop-shadow(0 0 6px rgba(124,108,255,.35)); }
  50% { filter: drop-shadow(0 0 16px rgba(124,108,255,.75)); } }

@media (max-width: 960px){ .neural-grid{ grid-template-columns: 1fr; } }
</style>
